import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  CheckCircle2, XCircle, ArrowUpRight, ArrowDownRight, Activity as ActivityIcon, Hand, Search, TerminalSquare,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip,
} from "recharts";
import type { LucideIcon } from "lucide-react";
import PixelAvatar from "@/shared/components/PixelAvatar";
import { Breadcrumb } from "@/shared/components/Breadcrumb";
import { PageHeader } from "@/shared/components/ui-kit";
import { useOverview } from "@/shared/api/overview";
import { UNFINISHED } from "@/shared/api/threads";
import { APP_ICONS, APP_LABEL } from "@/shared/lib/app-icons";
import { agentMood } from "@/shared/lib/agent-mood";
import type { Agent, ApiOverview, ApiOverviewRun, ApiRunStatus, AppId } from "@/shared/types";

/* ---------------------------------------------------------------------------
   The workspace's analytics, from one read (`GET /overview`, `useOverview`):
   per-day, per-agent runs, failures, tokens, spend and tool calls for the range
   and the range before it (the deltas), the tools called, each agent's state,
   and the newest runs. Every run's start and end re-reads it over the events
   socket, so the page is live without a poll. `backend/modules/overview/README.md`.
--------------------------------------------------------------------------- */

const RANGES = [
  { id: "7d", label: "7d", days: 7 },
  { id: "30d", label: "30d", days: 30 },
  { id: "90d", label: "90d", days: 90 },
];

// Categorical colors follow each agent's existing hue; the 4 known slots were
// validated (CVD ΔE 15.7 worst pair, ≥3:1 contrast on the light surface).
const AGENT_COLORS: Record<string, string> = {
  "research-analyst": "#2A78D6",
  "copy-editor": "#C2417C",
  "data-wrangler": "#1E8F5A",
  "trip-planner": "#C96F1F",
};
const colorFor = (agent: Agent) => AGENT_COLORS[agent.id] || `hsl(${agent.hue ?? 220} 46% 44%)`;

// Tools that aren't a connected app: the sandbox's (the server folds them into one
// key), and finding an app's tools.
const GENERIC_TOOL_META: Record<string, { label: string; Icon: LucideIcon }> = {
  sandbox: { label: "Sandbox", Icon: TerminalSquare },
  search_app_tools: { label: "App search", Icon: Search },
};

/* ------------------------------- helpers -------------------------------- */

/** One day of the chart axis: the fixed aggregate columns plus one tokens
    column per agent id, which is why the index signature is here. Every key
    `sum` is asked for is one of the numeric ones. */
interface DayRow {
  date: string;
  iso: string;
  tokens: number;
  cost: number;
  runs: number;
  toolCalls: number;
  failures: number;
  [agentId: string]: string | number;
}

const fmtNum = (n: number) =>
  n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : `${Math.round(n)}`;
const fmtUsd = (n: number) => `$${n >= 1000 ? fmtNum(n) : n.toFixed(2)}`;

const sum = (rows: DayRow[], key: string) => rows.reduce((acc, r) => acc + ((r[key] as number) || 0), 0);
/** Undefined with nothing before to compare with: the tile then shows no change at all. */
const pctDelta = (cur: number, prev: number) => (prev === 0 ? undefined : ((cur - prev) / prev) * 100);

/** Today's runs by the clock, older ones by how many days back. */
function shortWhen(startedAt: string) {
  const at = dayjs(startedAt);
  const days = dayjs().startOf("day").diff(at.startOf("day"), "day");
  return days <= 0 ? at.format("HH:mm") : `${days}d ago`;
}

const titleOf = (run: ApiOverviewRun) => run.title || "Untitled chat";
const firstLine = (text: string) => text.split("\n").find((line) => line.trim())?.trim() ?? "";

/* --------------------------- data derivation ----------------------------- */

/** One agent's range: what the table row and the chart legend show. */
interface AgentStats {
  runs: number;
  failures: number;
  tokens: number;
  cost: number;
  toolCalls: number;
  /** null when it didn't run in the range — the table shows an em dash. */
  successRate: number | null;
  /** A run of it is in flight right now. */
  running: boolean;
  lastStatus: ApiRunStatus | null;
  channelsLive: number;
}

interface AgentSeries {
  agent: Agent;
  stats: AgentStats;
  color: string;
}

const NO_RUNS = { runs: 0, failures: 0, tokens: 0, cost: 0, toolCalls: 0 };

/** Every day from `since` to `today`, none missing: the read leaves out days
    nobody ran on, and a chart needs them as zeros. */
function buildDays(data: ApiOverview, known: (agentId: string) => boolean): DayRow[] {
  const days: DayRow[] = [];
  const byIso: Record<string, DayRow> = {};
  for (let d = dayjs(data.since); !d.isAfter(dayjs(data.today)); d = d.add(1, "day")) {
    const row: DayRow = { date: d.format("MMM D"), iso: d.format("YYYY-MM-DD"), tokens: 0, cost: 0, runs: 0, toolCalls: 0, failures: 0 };
    days.push(row);
    byIso[row.iso] = row;
  }
  for (const r of data.daily) {
    const row = byIso[r.date];
    if (!row || !known(r.agent_id)) continue;
    row[r.agent_id] = r.tokens;
    row.tokens += r.tokens;
    row.cost += r.cost_usd;
    row.runs += r.runs;
    row.toolCalls += r.tool_calls;
    row.failures += r.failures;
  }
  return days;
}

/** One line of the "Recent activity" feed. */
interface FeedEvent {
  agent: Agent;
  run: ApiOverviewRun;
  when: string;
  icon: LucideIcon;
  tone: string;
  text: string;
}

// A run → one human-readable feed event: how it went, and the start of its answer.
function runEvent(run: ApiOverviewRun, agentById: Record<string, Agent>): FeedEvent | null {
  const agent = agentById[run.agent_id];
  if (!agent) return null;
  const base = { agent, run, when: shortWhen(run.started_at) };
  if (run.status === "error") {
    return { ...base, icon: XCircle, tone: "text-[var(--danger)]", text: `Run failed — ${titleOf(run)}` };
  }
  if (UNFINISHED.includes(run.status)) {
    return { ...base, icon: ActivityIcon, tone: "text-[var(--info-text)]", text: `Running — ${titleOf(run)}` };
  }
  if (run.status === "pending") {
    return { ...base, icon: Hand, tone: "text-[var(--warn-text)]", text: `Waiting for you — ${titleOf(run)}` };
  }
  if (run.status === "halted") {
    return { ...base, icon: CheckCircle2, tone: "text-[var(--text-tertiary)]", text: `Stopped — ${titleOf(run)}` };
  }
  const detail = run.summary
    ? firstLine(run.summary).replace(/\*\*|__|`|^[#>\s]+/g, "").split(/(?<=[.!?])\s/)[0].replace(/[.!?]$/, "")
    : null;
  return {
    ...base,
    icon: CheckCircle2,
    tone: "text-[var(--success)]",
    text: detail ? `${titleOf(run)} — ${detail}` : `Completed run — ${titleOf(run)}`,
  };
}

/** Everything the page shows, from one read. `daily` is the range and the one
    before it, halved here — so while another range loads, the one on screen
    still adds up. An agent the read names but the workspace no longer lists
    (deleted since) is left out. */
function buildModel(agents: Agent[], data: ApiOverview) {
  const agentById: Record<string, Agent> = Object.fromEntries(agents.map((a) => [a.id, a]));
  const known = (agentId: string) => agentId in agentById;
  const allDays = buildDays(data, known);
  const half = allDays.length / 2;
  const days = allDays.slice(half);
  const prev = allDays.slice(0, half);

  const inRange: Record<string, typeof NO_RUNS> = {};
  for (const r of data.daily) {
    if (r.date < days[0].iso) continue;
    const own = (inRange[r.agent_id] ??= { ...NO_RUNS });
    own.runs += r.runs;
    own.failures += r.failures;
    own.tokens += r.tokens;
    own.cost += r.cost_usd;
    own.toolCalls += r.tool_calls;
  }
  const stateById = Object.fromEntries(data.agents.map((state) => [state.agent_id, state]));
  const series: AgentSeries[] = agents.map((agent) => {
    const own = inRange[agent.id] ?? NO_RUNS;
    const state = stateById[agent.id];
    return {
      agent,
      color: colorFor(agent),
      stats: {
        ...own,
        successRate: own.runs ? ((own.runs - own.failures) / own.runs) * 100 : null,
        running: !!state?.running,
        lastStatus: state?.last_status ?? null,
        channelsLive: state?.channels_live ?? 0,
      },
    };
  });

  const mk = (key: string) => {
    const c = sum(days, key);
    return { cur: c, delta: pctDelta(c, sum(prev, key)) };
  };
  const runsK = mk("runs");
  const failures = mk("failures");
  const failRate = runsK.cur > 0 ? (failures.cur / runsK.cur) * 100 : 0;
  const prevRuns = sum(prev, "runs");
  // no runs before: no rate to compare with (a rate of 0 would be one)
  const prevFailRate = prevRuns > 0 ? (sum(prev, "failures") / prevRuns) * 100 : undefined;

  // `filter(Boolean)` drops the nulls — TS can't see that through `Boolean`.
  const events = data.recent.map((r) => runEvent(r, agentById)).filter(Boolean) as FeedEvent[];
  const running = series.filter((s) => s.stats.running).length;
  const worst = series.reduce<AgentSeries | null>(
    (best, s) => (s.stats.failures > (best?.stats.failures || 0) ? s : best),
    null
  );
  return {
    series,
    chartAgents: series.filter((s) => s.stats.tokens > 0),
    days,
    kpis: {
      runs: runsK,
      tokens: mk("tokens"),
      cost: mk("cost"),
      toolCalls: mk("toolCalls"),
      failRate: { cur: failRate, delta: prevFailRate === undefined ? undefined : pctDelta(failRate, prevFailRate) },
    },
    usage: data.tools.slice(0, 7),
    toolKinds: data.tools.length,
    failureRuns: data.failures.filter((r) => known(r.agent_id)),
    events,
    running,
    idle: series.length - running,
    errored: series.filter((s) => s.stats.lastStatus === "error").length,
    worstAgent: worst && worst.stats.failures > 0 ? worst.agent : null,
    channelsLive: series.reduce((n, s) => n + s.stats.channelsLive, 0),
    reachable: series.filter((s) => s.stats.channelsLive > 0).length,
  };
}

type Model = ReturnType<typeof buildModel>;

/* ------------------------------ atoms ----------------------------------- */

function Delta({ value, invert = false }: { value: number; invert?: boolean }) {
  const up = value >= 0;
  const good = invert ? !up : up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11.5px] font-medium tabular-nums ${
        good ? "text-[var(--success)]" : "text-[var(--danger)]"
      }`}
    >
      <Icon className="w-3 h-3" strokeWidth={2} />
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function StatTile({ label, value, delta, invert, hint, onClick }: {
  label: React.ReactNode;
  value: React.ReactNode;
  delta?: number;
  invert?: boolean;
  hint?: React.ReactNode;
  onClick?: () => void;
}) {
  const Comp: React.ElementType = onClick ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      className={`card-hover rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] px-4 py-3.5 min-w-0 text-left ${
        onClick ? "press cursor-pointer w-full" : ""
      }`}
    >
      <div className="section-label">{label}</div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="text-[22px] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">{value}</span>
        {delta !== undefined && <Delta value={delta} invert={invert} />}
      </div>
      {hint && <div className="mt-0.5 text-[11.5px] leading-[1.45] text-[var(--text-tertiary)] line-clamp-2">{hint}</div>}
    </Comp>
  );
}

function Card({ title, aside, children, className = "" }: {
  title: React.ReactNode;
  aside?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] flex flex-col min-w-0 ${className}`}>
      {/* The aside (a legend, a count) drops under the title when both don't fit on one line */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 pt-3.5 pb-1">
        <div className="text-[13px] font-medium text-[var(--text-primary)] tracking-[-0.005em] flex-1 whitespace-nowrap">{title}</div>
        {aside}
      </div>
      {children}
    </div>
  );
}

/** Recharts injects `active`/`payload`/`label` into a custom tooltip; `money`
    is ours. Typed to the fields this renderer actually reads. */
interface ChartTipProps {
  active?: boolean;
  payload?: { dataKey?: string | number; name?: string; value: number; color?: string; fill?: string }[];
  label?: React.ReactNode;
  money?: boolean;
}

function ChartTip({ active, payload, label, money }: ChartTipProps) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => p.value > 0);
  const total = rows.reduce((a, p) => a + p.value, 0);
  return (
    <div className="rounded-[10px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] shadow-menu px-3 py-2 text-[12px]">
      <div className="text-[var(--text-tertiary)] mb-1">{label}</div>
      {rows.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-1.5 py-[1px]">
          <span className="w-2 h-2 rounded-[3px]" style={{ background: p.color || p.fill }} />
          <span className="text-[var(--text-secondary)] flex-1 pr-3">{p.name}</span>
          <span className="font-medium tabular-nums text-[var(--text-primary)]">
            {money ? fmtUsd(p.value) : fmtNum(p.value)}
          </span>
        </div>
      ))}
      {rows.length > 1 && (
        <div className="flex items-center gap-1.5 pt-1 mt-1 border-t hairline">
          <span className="text-[var(--text-secondary)] flex-1 pr-3">Total</span>
          <span className="font-medium tabular-nums">{money ? fmtUsd(total) : fmtNum(total)}</span>
        </div>
      )}
    </div>
  );
}

const AXIS = {
  tickLine: false,
  axisLine: false,
  tick: { fontSize: 11, fill: "var(--text-tertiary)" },
};

/* ------------------------------ page ------------------------------------ */

export default function OverviewPage({ agents = [], onOpenTraces }: {
  agents?: Agent[];
  onOpenTraces?: (agentId: string, runId?: string | null) => void;
}) {
  const [rangeId, setRangeId] = useState("30d");
  // The tab strip only ever sets an id from RANGES, so the lookup always hits.
  const range = RANGES.find((r) => r.id === rangeId)!;
  const { data } = useOverview(range.days);
  return <OverviewView agents={agents} data={data} rangeId={rangeId} onRangeChange={setRangeId} onOpenTraces={onOpenTraces} />;
}

/** The page without its read — what the stories render from a fixture. `data`
    is undefined until the first read lands, and nothing is shown for it. */
export function OverviewView({ agents, data, rangeId, onRangeChange, onOpenTraces }: {
  agents: Agent[];
  data: ApiOverview | undefined;
  rangeId: string;
  onRangeChange: (rangeId: string) => void;
  onOpenTraces?: (agentId: string, runId?: string | null) => void;
}) {
  const range = RANGES.find((r) => r.id === rangeId)!;
  const model = useMemo(() => (data ? buildModel(agents, data) : null), [agents, data]);

  return (
    <main className="flex-1 min-w-0 flex flex-col bg-[var(--canvas)]">
      {/* Header */}
      <PageHeader
        title={<Breadcrumb>Overview</Breadcrumb>}
      >
        <div className="flex items-center rounded-[9px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] p-[3px]">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => onRangeChange(r.id)}
              className={`press px-2.5 py-[3px] rounded-[7px] text-[12.5px] font-medium tabular-nums ${
                r.id === rangeId
                  ? "bg-[var(--surface)] text-[var(--text-primary)] shadow-[0_1px_2px_rgba(0,0,0,0.03)] ring-1 ring-[var(--hairline)]"
                  : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </PageHeader>

      {agents.length === 0 || model?.events.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 fade-in">
          <div className="w-10 h-10 rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] flex items-center justify-center">
            <ActivityIcon className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.75} />
          </div>
          <div className="text-center">
            <div className="text-[15px] font-medium text-[var(--text-primary)] tracking-[-0.005em]">
              {agents.length === 0 ? "No agents yet" : "No activity yet"}
            </div>
            <div className="text-[13px] text-[var(--text-tertiary)] mt-1 max-w-[320px]">
              {agents.length === 0
                ? "Create an agent to start seeing usage, spend, and failures here."
                : "Once your agents start running — from chats or scheduled tasks — usage, spend, and failures show up here."}
            </div>
          </div>
        </div>
      ) : model ? (
        <Dashboard model={model} rangeDays={range.days} onOpenTraces={onOpenTraces} />
      ) : null}
    </main>
  );
}

function Dashboard({ model, rangeDays, onOpenTraces }: {
  model: Model;
  rangeDays: number;
  onOpenTraces?: (agentId: string, runId?: string | null) => void;
}) {
  const { days, kpis } = model;
  const totalToolCalls = kpis.toolCalls.cur;
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-[1160px] mx-auto px-4 md:px-6 py-6 space-y-4 fade-in-up">
        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          <StatTile
            label="Agents running"
            value={`${model.running} / ${model.series.length}`}
            hint={`${model.idle} idle · ${model.errored} errored`}
          />
          <StatTile
            label="Channels live"
            value={model.channelsLive}
            hint={`${model.reachable} reachable agents`}
          />
          <StatTile label="Runs" value={fmtNum(kpis.runs.cur)} delta={kpis.runs.delta} hint={`last ${rangeDays} days`} />
          <StatTile label="Spend" value={fmtUsd(kpis.cost.cur)} delta={kpis.cost.delta} invert hint="all models + tools" />
          <StatTile label="Tokens" value={fmtNum(kpis.tokens.cur)} delta={kpis.tokens.delta} hint="input + output" />
          <StatTile
            label="Failure rate"
            value={`${kpis.failRate.cur.toFixed(1)}%`}
            delta={kpis.failRate.delta}
            invert
            hint={
              model.worstAgent
                ? `most errors: ${model.worstAgent.name}`
                : `${fmtNum(sum(days, "failures"))} failed runs`
            }
            onClick={
              // The guard is on the same expression; narrowing just doesn't
              // survive into the closure.
              model.worstAgent && onOpenTraces ? () => onOpenTraces(model.worstAgent!.id) : undefined
            }
          />
          <StatTile label="Tool calls" value={fmtNum(totalToolCalls)} delta={kpis.toolCalls.delta} hint={`across ${model.toolKinds} apps`} />
        </div>

        {/* Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Card
            title="Token consumption"
            className="lg:col-span-2"
            aside={
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {model.chartAgents.map(({ agent, color }) => (
                  <span key={agent.id} className="flex items-center gap-1.5 whitespace-nowrap text-[11.5px] text-[var(--text-secondary)]">
                    <span className="w-2 h-2 rounded-[3px]" style={{ background: color }} />
                    {agent.name}
                  </span>
                ))}
              </div>
            }
          >
            <div className="h-[236px] px-2 pb-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={days} margin={{ top: 12, right: 8, left: 0, bottom: 0 }} barCategoryGap="28%">
                  <CartesianGrid vertical={false} stroke="var(--hairline)" />
                  <XAxis dataKey="date" {...AXIS} minTickGap={32} />
                  <YAxis {...AXIS} width={48} tickFormatter={fmtNum} />
                  <Tooltip content={<ChartTip />} cursor={{ fill: "var(--accent-hover)" }} />
                  {model.chartAgents.map(({ agent, color }, i) => (
                    <Bar
                      key={agent.id}
                      dataKey={agent.id}
                      name={agent.name}
                      stackId="tokens"
                      fill={color}
                      stroke="var(--surface)"
                      strokeWidth={1}
                      radius={i === model.chartAgents.length - 1 ? [3, 3, 0, 0] : 0}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Spend" aside={<span className="text-[11.5px] text-[var(--text-tertiary)] tabular-nums">{fmtUsd(kpis.cost.cur)} total</span>}>
            <div className="h-[236px] px-2 pb-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={days} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--text-primary)" stopOpacity={0.09} />
                      <stop offset="100%" stopColor="var(--text-primary)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="var(--hairline)" />
                  <XAxis dataKey="date" {...AXIS} minTickGap={32} />
                  <YAxis {...AXIS} width={48} tickFormatter={(v) => `$${v}`} />
                  <Tooltip content={<ChartTip money />} cursor={{ stroke: "var(--hairline-strong)" }} />
                  <Area
                    type="monotone"
                    dataKey="cost"
                    name="Spend"
                    stroke="var(--text-primary)"
                    strokeWidth={1.75}
                    fill="url(#spendFill)"
                    dot={false}
                    activeDot={{ r: 3, strokeWidth: 2, stroke: "var(--surface)" }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Tools · failures · activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Card title="Tool usage" aside={<span className="text-[11.5px] text-[var(--text-tertiary)] tabular-nums">{fmtNum(totalToolCalls)} calls</span>}>
            <div className="px-4 pb-4 pt-1 space-y-2.5">
              {model.usage.length === 0 && (
                <div className="py-1 text-[12px] text-[var(--text-quaternary)] italic">No tool calls yet</div>
              )}
              {model.usage.map((t) => {
                  const pct = (t.calls / (model.usage[0]?.calls || 1)) * 100;
                // `t.key` is a tool-name prefix, so it is only sometimes an
                // AppId — the two maps below are probed, and a miss falls
                // through to the generic families. (Same probe smell as CityView.)
                const generic = GENERIC_TOOL_META[t.key];
                const GenericIcon = generic?.Icon;
                return (
                  <div key={t.key} className="flex items-center gap-2.5">
                    <div className="w-[18px] h-[18px] rounded-[5px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] flex items-center justify-center shrink-0">
                      {APP_ICONS[t.key as AppId] ? (
                        <img src={APP_ICONS[t.key as AppId]} alt="" className="w-[11px] h-[11px]" />
                      ) : GenericIcon ? (
                        <GenericIcon className="w-[11px] h-[11px] text-[var(--text-tertiary)]" strokeWidth={1.75} />
                      ) : (
                        <span className="w-[11px] h-[11px]" />
                      )}
                    </div>
                    <span className="text-[13px] text-[var(--text-secondary)] w-[92px] truncate">
                      {APP_LABEL[t.key as AppId] || generic?.label || t.key}
                    </span>
                    <div className="flex-1 h-[6px] rounded-full bg-[var(--surface-2)] overflow-hidden">
                      <div className="h-full rounded-full bg-[var(--brand)] opacity-80" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[12px] font-medium tabular-nums text-[var(--text-primary)] w-10 text-right">{fmtNum(t.calls)}</span>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card
            title="Failures"
            aside={
              <span className="flex items-center gap-1 text-[11.5px] text-[var(--text-tertiary)]">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--danger)]" />
                {fmtNum(sum(days, "failures"))} failed · {kpis.failRate.cur.toFixed(1)}%
              </span>
            }
          >
            <div className="h-[120px] px-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={days} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
                  <XAxis dataKey="date" {...AXIS} minTickGap={40} />
                  <YAxis {...AXIS} width={26} allowDecimals={false} />
                  <Tooltip content={<ChartTip />} cursor={{ fill: "var(--accent-hover)" }} />
                  <Bar dataKey="failures" name="Failed runs" fill="#d03b3b" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="px-3 pb-3 pt-2 space-y-[2px] border-t hairline mt-1">
              {model.failureRuns.length === 0 && (
                <div className="px-1 py-1 text-[12px] text-[var(--text-quaternary)] italic">No failed runs</div>
              )}
              {model.failureRuns.map((run) => {
                return (
                  <button
                    key={run.run_id}
                    onClick={() => onOpenTraces?.(run.agent_id, run.run_id)}
                    className="press w-full flex items-center gap-2 text-[12px] text-left px-1 py-[4px] rounded-[6px] hover:bg-[var(--accent-hover)]"
                  >
                    <XCircle className="w-3 h-3 text-[var(--danger)] shrink-0" strokeWidth={1.75} />
                    <span className="text-[var(--text-secondary)] truncate flex-1">
                      {titleOf(run)}
                      {run.error ? ` — ${firstLine(run.error)}` : ""}
                    </span>
                    <span className="text-[var(--text-quaternary)] tabular-nums shrink-0">{shortWhen(run.started_at)}</span>
                  </button>
                );
              })}
            </div>
          </Card>

          <Card title="Recent activity">
            <div className="px-3 pb-4 pt-1 space-y-[2px]">
              {model.events.map((a) => (
                <button
                  key={a.run.run_id}
                  onClick={() => onOpenTraces?.(a.run.agent_id, a.run.run_id)}
                  className="press w-full flex items-center gap-2.5 px-1 py-[5px] rounded-[8px] text-left hover:bg-[var(--accent-hover)]"
                >
                  <a.icon className={`w-3.5 h-3.5 shrink-0 ${a.tone}`} strokeWidth={1.75} />
                  <PixelAvatar seed={a.agent.seed} hue={a.agent.hue} emotion={agentMood(a.agent)} size={16} rounded={4} />
                  <span className="text-[12.5px] text-[var(--text-secondary)] truncate flex-1">{a.text}</span>
                  <span className="text-[11px] text-[var(--text-quaternary)] tabular-nums shrink-0">{a.when}</span>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Per-agent table */}
        <Card
          title="Agents"
          aside={
            <span className="text-[11.5px] text-[var(--text-tertiary)]">
              {model.running} running · {model.idle} idle
            </span>
          }
        >
          {/* Rows stay one line and the table scrolls sideways on a phone, rather than stacking every word */}
          <div className="px-4 pb-3 overflow-x-auto">
            <table className="w-full text-[13px] whitespace-nowrap">
              <thead>
                <tr className="text-left">
                  {["Agent", "Status", "Runs", "Tokens", "Tool calls", "Spend", "Success"].map((h, i) => (
                    <th key={h} className={`section-label font-medium py-2 ${i > 1 ? "text-right" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {model.series.map(({ agent, stats, color }) => {
                  const isError = stats.lastStatus === "error";
                  return (
                    <tr
                      key={agent.id}
                      onClick={() => onOpenTraces?.(agent.id)}
                      className="border-t hairline cursor-pointer hover:bg-[var(--accent-hover)] transition-colors"
                    >
                      <td className="py-2.5">
                        <div className="flex items-center gap-2.5">
                          <PixelAvatar seed={agent.seed} hue={agent.hue} emotion={agentMood(agent)} size={20} ring rounded={5} />
                          <span className="font-medium text-[var(--text-primary)]">{agent.name}</span>
                          {stats.tokens > 0 && <span className="w-2 h-2 rounded-[3px]" style={{ background: color }} />}
                        </div>
                      </td>
                      <td>
                        <span className="inline-flex items-center gap-1.5 text-[12px] text-[var(--text-secondary)]">
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              isError ? "bg-[var(--danger)]" : stats.running ? "bg-[var(--success)]" : "bg-[var(--text-quaternary)]"
                            }`}
                          />
                          {isError ? "Errored" : stats.running ? "Running" : "Idle"}
                        </span>
                      </td>
                      <td className="text-right tabular-nums">{fmtNum(stats.runs)}</td>
                      <td className="text-right tabular-nums">{fmtNum(stats.tokens)}</td>
                      <td className="text-right tabular-nums">{fmtNum(stats.toolCalls)}</td>
                      <td className="text-right tabular-nums">{fmtUsd(stats.cost)}</td>
                      <td className="text-right">
                        {stats.successRate == null ? (
                          <span className="tabular-nums text-[12px] text-[var(--text-quaternary)]">—</span>
                        ) : (
                          <div className="inline-flex items-center gap-2">
                            <div className="w-16 h-[5px] rounded-full bg-[var(--surface-2)] overflow-hidden">
                              <div className="h-full rounded-full bg-[var(--success)] opacity-80" style={{ width: `${stats.successRate}%` }} />
                            </div>
                            <span className="tabular-nums text-[12px] w-10 text-right">{stats.successRate.toFixed(1)}%</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
